/**
 * System Boot Sequence Component
 * 
 * Provides engaging visual feedback during Tier 2 service initialization.
 * Uses terminal-style animations to make the 1-2s load feel intentional and premium.
 * 
 * Features:
 * - Terminal-style typing animation
 * - Progress bar with smooth transitions
 * - Step-by-step service initialization display
 * - Particle effects for cyberpunk aesthetic
 * 
 * Phase 4 Architecture - Performance Optimization
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, Loader2, Circle } from 'lucide-react';
import { cn } from '@flowstate/utils';

interface BootStep {
  label: string;
  status: 'pending' | 'loading' | 'complete';
  progress: number;
}

interface SystemBootSequenceProps {
  progress?: number;
  isComplete?: boolean;
  onComplete?: () => void;
}

const bootSteps: { label: string; minProgress: number }[] = [
  { label: 'Authenticating credentials', minProgress: 5 },
  { label: 'Establishing secure connection', minProgress: 10 },
  { label: 'Initializing task management core', minProgress: 30 },
  { label: 'Loading notification system', minProgress: 45 },
  { label: 'Configuring workspace environment', minProgress: 60 },
  { label: 'Synchronizing real-time services', minProgress: 75 },
  { label: 'Preparing time management tools', minProgress: 85 },
  { label: 'Finalizing workspace setup', minProgress: 95 },
  { label: 'System ready', minProgress: 100 },
];

export const SystemBootSequence: React.FC<SystemBootSequenceProps> = ({
  progress = 0,
  isComplete = false,
  onComplete,
}) => {
  const [steps, setSteps] = useState<BootStep[]>(
    bootSteps.map((step, index) => ({
      label: step.label,
      status: index === 0 ? 'loading' : 'pending',
      progress: step.minProgress,
    }))
  );

  // Update steps based on progress
  useEffect(() => {
    setSteps(prevSteps =>
      prevSteps.map(step => {
        if (progress >= step.progress) {
          return { ...step, status: 'complete' };
        } else if (progress >= step.progress - 15) {
          return { ...step, status: 'loading' };
        }
        return { ...step, status: 'pending' };
      })
    );
  }, [progress]);

  // Trigger completion callback
  useEffect(() => {
    if (isComplete && onComplete) {
      const timer = setTimeout(onComplete, 500);
      return () => clearTimeout(timer);
    }
    return;
  }, [isComplete, onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="relative w-full max-w-lg bg-background rounded-xl shadow-2xl border overflow-hidden"
    >
      {/* Header gradient */}
      <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />

      <div className="relative z-10 p-6">
        {/* Terminal-style header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <h1 className="text-4xl font-bold tracking-tight mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
            FlowState Automate
          </h1>
          <p className="text-muted-foreground text-sm font-mono">
            Initializing workspace environment...
          </p>
        </motion.div>

        {/* Progress bar */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8 h-2 bg-muted rounded-full overflow-hidden"
        >
          <motion.div
            className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        </motion.div>

        {/* Boot steps */}
        <div className="space-y-2 font-mono text-sm">
          <AnimatePresence mode="popLayout">
            {steps.map((step, index) => (
              <motion.div
                key={step.label}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg transition-colors',
                  step.status === 'complete' && 'text-green-600 dark:text-green-400',
                  step.status === 'loading' && 'text-primary bg-primary/5',
                  step.status === 'pending' && 'text-muted-foreground'
                )}
              >
                {step.status === 'complete' && (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </motion.div>
                )}
                {step.status === 'loading' && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {step.status === 'pending' && (
                  <Circle className="h-4 w-4 opacity-30" />
                )}
                
                <span className="flex-1">{step.label}</span>
                
                {step.status === 'complete' && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-xs text-green-600 dark:text-green-400"
                  >
                    OK
                  </motion.span>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Progress percentage */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center text-2xl font-bold font-mono text-muted-foreground"
        >
          {Math.round(progress)}%
        </motion.div>

        {/* Powered by indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 text-center text-xs text-muted-foreground"
        >
          Powered by Phase 4 Architecture
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SystemBootSequence;
